import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Form, Container, Button, Row, Col } from 'react-bootstrap';
import { verifySecurityQuestions, fetchSecurityQuestions } from '../Auth/store/Auth.action';
import './Question.css';
import logo from '../../assets/images/bombiix-logo.svg';

/**
 * Un-signed-in screen where a user answers their security questions during a
 * password reset. The reset-link id from the forgot-password email proves the
 * page may read the account's questions — without it the server refuses and
 * the form stays empty — and it is carried on to the change-password step.
 * @returns the answer-security-questions view.
 */
function AnswerSecurityQuestions() {
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const history = useHistory();
  const {
    UserDetails: { securityQuestion },
  } = useSelector((state) => state);

  const searchParams = new URLSearchParams(window?.location?.search);
  let email = searchParams.get('email');
  if (email) email = decodeURIComponent(email);
  const id = searchParams.get('id');

  useEffect(() => {
    if (!email) return;
    dispatch(fetchSecurityQuestions({ email: encodeURIComponent(email), id }, t));
  }, [dispatch, email, id]);

  useEffect(() => {
    if (!securityQuestion?.length) return;
    const ques = securityQuestion.map((question) => ({
      question,
    }));
    setSelectedQuestions(ques);
  }, [securityQuestion]);

  const checkErrors = (index, value) => {
    if (!value?.trim()?.length) {
      setErrors((prev) => {
        const arr = [...prev];
        arr[index] = t('QUSTIONS.ANSWER_SECURITY_QUSTIONS.ANSWER_REQUIRED_ERROR');
        return arr;
      });
      return false;
    }
    setErrors((prev) => {
      const arr = [...prev];
      arr.splice(index, 1);
      return arr;
    });
    return true;
  };

  const handleInput = (e, index) => {
    const { name, value } = e.target;
    setSelectedQuestions((prev) => {
      const newArray = [...prev];
      newArray[index] = { ...newArray[index], [name]: value };
      return newArray;
    });

    checkErrors(index, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    const isInvalid = selectedQuestions.reduce((acc, obj, i) => {
      if (!checkErrors(i, obj.answer)) return true;
      return acc;
    }, false);
    if (isInvalid) return;

    const data = { questions: selectedQuestions, email };
    const res = await dispatch(verifySecurityQuestions(data, t));
    if (res) {
      history.push(`/change-password?email=${encodeURIComponent(email)}&id=${id}&verifiedQuestion=true`);
    }
  };

  const formControl = (index) => {
    return (
      <Row>
        <Col className="mb-0">
          <Form.Group>
            <Form.Control as="select" name="question" disabled className="mb-1">
              <option value="default">{selectedQuestions[index]?.question}</option>
            </Form.Control>
            <Form.Control
              type="text"
              name="answer"
              placeholder={t('QUSTIONS.ANSWER_SECURITY_QUSTIONS.ANSWER_PLACEHOLDER')}
              onChange={(e) => handleInput(e, index)}
            />
            {isSubmitted && <Form.Text className="text-danger">{errors[index]}</Form.Text>}
          </Form.Group>
        </Col>
      </Row>
    );
  };

  return (
    <Container>
      <div className="login-container-bombiix">
        <div className="logo-container">
          <img src={logo} className="img-fluid logo-image" alt="logo" />
        </div>
        <div className="mt-40">
          <p className="text-center text-medium">{t('QUSTIONS.ANSWER_SECURITY_QUSTIONS.TITLE')}</p>
          <Form onSubmit={handleSubmit}>
            {formControl(0)}
            {formControl(1)}
            <div className="text-center">
              <Button type="submit" className="btn-theme" onSubmit={handleSubmit}>
                {t('QUSTIONS.ANSWER_SECURITY_QUSTIONS.SUBMIT_BUTTON')}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
}

export default AnswerSecurityQuestions;
